import { Button, Stack, Tooltip } from '@mui/material';
import { LoadingButton } from 'probonio-shared-ui/component/button';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { TextFieldControl } from '../../../component/form';
import { ITLeasingFormControls, ITLeasingFormValues, useITLeasingForm } from './useITLeasingForm';
import { useITLeasingPrecondition } from './useITLeasingPrecondition';

interface ITLeasingConnectFormProps {
  onSubmit?: () => void;
}

const ITLeasingConnectForm: React.FC<ITLeasingConnectFormProps> = ({ onSubmit }) => {
  const { t } = useTranslation('benefitsModule');
  const { isPreconditionMet } = useITLeasingPrecondition();
  const { form, createITLeasingCompanyLink, isLoading, disabled: formDisabled, handleReset } = useITLeasingForm({ onConnected: onSubmit });
  const resetDisabled = !isPreconditionMet || !form.formState.isDirty || isLoading;

  const handleSave = useCallback(
    (values: ITLeasingFormValues) => {
      createITLeasingCompanyLink.mutate(values);
    },
    [createITLeasingCompanyLink],
  );
  const linkRegex = /^https:\/\/(www\.)?service\.dedicom\.de\/konto\/registrieren\/.*$/i;

  return (
    <form onSubmit={form.handleSubmit(handleSave)} style={{ flexGrow: 1 }}>
      <TextFieldControl
        control={form.control}
        name={ITLeasingFormControls.itLeasingCompanyLink}
        sx={{ width: 500, mb: 2 }}
        slotProps={{
          htmlInput: {
            sx: { textAlign: 'center' },
          },
        }}
        label={t('IT_LEASING.connect.form.name')}
        rules={{
          maxLength: 255,
          validate: v => (!v ? t('IT_LEASING.connect.form.error') : linkRegex.test(v) || t('IT_LEASING.connect.form.error')),
        }}
        helperText={t('IT_LEASING.connect.form.hint')}
      />
      <Stack
        spacing={2}
        direction="row"
        sx={{
          justifyContent: 'center',
        }}
      >
        {isPreconditionMet && (
          <Button variant="text" size="small" type="reset" disabled={resetDisabled} onClick={handleReset}>
            {t('common:buttons.discard')}
          </Button>
        )}

        <Tooltip arrow placement="top" title={t('common:buttons.saveDisabledTooltip')} disableHoverListener={!formDisabled}>
          <span>
            <LoadingButton size="small" type="submit" disabled={formDisabled} loading={isLoading} data-test-id="settings-form-panel-save">
              {t('common:buttons.save')}
            </LoadingButton>
          </span>
        </Tooltip>
      </Stack>
    </form>
  );
};

export default ITLeasingConnectForm;
